// Photos and notes from the field bar (phone, on the road): kept on the phone first, then sent, so nothing is lost
// where there is no signal. Each one is sent with the person's own sign-in: the photo to the field photos
// (/v1/field/photos), then onto the job's Photos field; a note onto the end of the job's notes. A job that changed in
// the meantime is read again and the item added once more (the CRM's version check), never written over.
import type { CrmRecord, CrmType, Value } from "./crm";

export type FieldItem = {
  id: string; kind: "photo" | "note"; jobType: string; jobId: string; jobTitle: string; at: number;
  dataUrl?: string; text?: string; tries: number;
};

/** Where waiting items live: IndexedDB on the phone (memory when there is none, as in tests). */
export interface QueueStore {
  all(): Promise<FieldItem[]>;
  put(item: FieldItem): Promise<void>;
  remove(id: string): Promise<void>;
}

/** What sending needs, with the person's sign-in already applied. */
export type FieldDeps = {
  upload: (dataUrl: string, note: string) => Promise<string>;
  get: (type: string, id: string) => Promise<CrmRecord>;
  update: (type: string, id: string, version: number, values: Record<string, Value>) => Promise<CrmRecord>;
  types: CrmType[];
};

/** After this many failed tries an item stays on the phone but is no longer sent automatically. */
export const MAX_TRIES = 25;

export function memoryStore(): QueueStore {
  const items = new Map<string, FieldItem>();
  return {
    all: async () => [...items.values()].sort((a, b) => a.at - b.at),
    put: async (i) => { items.set(i.id, i); },
    remove: async (id) => { items.delete(id); },
  };
}

export function indexedDbStore(name = "vera-field-queue"): QueueStore | null {
  if (typeof indexedDB === "undefined") return null;
  const open = () => new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(name, 1);
    req.onupgradeneeded = () => { if (!req.result.objectStoreNames.contains("items")) req.result.createObjectStore("items", { keyPath: "id" }); };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  const run = async <T>(mode: IDBTransactionMode, work: (s: IDBObjectStore) => IDBRequest<T>) => {
    const db = await open();
    try {
      return await new Promise<T>((resolve, reject) => {
        const req = work(db.transaction("items", mode).objectStore("items"));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
    } finally { db.close(); }
  };
  return {
    all: async () => ((await run("readonly", (s) => s.getAll())) as FieldItem[]).sort((a, b) => a.at - b.at),
    put: async (i) => { await run("readwrite", (s) => s.put(i)); },
    remove: async (id) => { await run("readwrite", (s) => s.delete(id)); },
  };
}

/** The job's photos field and its notes field: the first of each kind (Marine's jobs have "photos" and "notes"). */
export const photosField = (t: CrmType | undefined) => t?.fields.find((f) => f.kind === "photos" && !f.hidden);
export const notesField = (t: CrmType | undefined) =>
  t?.fields.find((f) => f.kind === "longtext" && /note/i.test(f.id) && !f.hidden) ?? t?.fields.find((f) => f.kind === "longtext" && !f.hidden);

/** A note added to the end of what's there, with when it was said: "26 Oct, 3:04 PM: …". */
export function withNote(existing: Value | undefined, text: string, at: Date, locale?: string): string {
  const stamp = at.toLocaleString(locale, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
  const before = typeof existing === "string" ? existing.trimEnd() : "";
  return `${before ? `${before}\n\n` : ""}${stamp}: ${text.trim()}`;
}

/** A photo id added to the job's photos, once. */
export function withPhoto(existing: Value | undefined, photoId: string): string[] {
  const ids = Array.isArray(existing) ? existing.map(String) : [];
  return ids.includes(photoId) ? ids : [...ids, photoId];
}

const isConflict = (e: unknown) => e instanceof Error && /changed|version|conflict|409/i.test(e.message);

/** Sends one item: "sent", or "wait" (try again later). A photo uploaded once keeps its id across tries. */
export async function sendItem(item: FieldItem, deps: FieldDeps, store?: QueueStore): Promise<"sent" | "wait"> {
  const type = deps.types.find((t) => t.id === item.jobType);
  const field = item.kind === "photo" ? photosField(type) : notesField(type);
  if (!field) return "wait";
  try {
    let text = item.text ?? "";
    if (item.kind === "photo" && item.dataUrl) {
      // Upload first; then the photo id replaces the image, so a retry never uploads it twice.
      const photoId = await deps.upload(item.dataUrl, item.text ? `Field: ${item.text}` : "Field");
      item = { ...item, dataUrl: undefined, text: `photo:${photoId}` };
      await store?.put(item);
      text = item.text!;
    }
    for (let attempt = 0; attempt < 2; attempt++) {
      const job = await deps.get(item.jobType, item.jobId);
      const value = item.kind === "photo"
        ? withPhoto(job.values[field.id], text.replace(/^photo:/, ""))
        : withNote(job.values[field.id], text, new Date(item.at));
      try {
        await deps.update(item.jobType, item.jobId, job.version, { [field.id]: value });
        return "sent";
      } catch (e) {
        if (!isConflict(e) || attempt === 1) throw e;
      }
    }
    return "wait";
  } catch {
    return "wait";
  }
}

/** Sends everything waiting, oldest first. Returns how many went and how many still wait. */
export async function flush(store: QueueStore, deps: FieldDeps): Promise<{ sent: number; waiting: number }> {
  let sent = 0;
  for (const item of await store.all()) {
    if (item.tries >= MAX_TRIES) continue;
    if ((await sendItem(item, deps, store)) === "sent") { await store.remove(item.id); sent++; }
    else {
      const latest = (await store.all()).find((i) => i.id === item.id) ?? item;
      await store.put({ ...latest, tries: latest.tries + 1 });
      if (typeof navigator !== "undefined" && navigator.onLine === false) break;
    }
  }
  return { sent, waiting: (await store.all()).length };
}
